/* =========================================================================
   toast.tsx — Toast notification system
   Usage: const toast = useToast(); toast.success("Saved!");
   ========================================================================= */

import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextValue {
  show: (message: string, type?: ToastType, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be inside ToastProvider");
  return ctx;
}

let _globalToast: ToastContextValue | null = null;

/** Escape hatch for calling toast outside React components (e.g. in async callbacks). */
export function toastGlobal(
  message: string,
  type: ToastType = "info",
  duration = 4000,
) {
  _globalToast?.show(message, type, duration);
}

const ICONS: Record<ToastType, React.ReactNode> = {
  success: (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  error: (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9l-6 6M9 9l6 6" />
    </svg>
  ),
  warning: (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  info: (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
};

const STYLES: Record<
  ToastType,
  { bg: string; border: string; text: string; icon: string }
> = {
  success: {
    bg: "hsl(var(--c-success) / 0.10)",
    border: "hsl(var(--c-success) / 0.25)",
    text: "hsl(var(--c-success))",
    icon: "hsl(var(--c-success))",
  },
  error: {
    bg: "hsl(var(--c-destructive) / 0.10)",
    border: "hsl(var(--c-destructive) / 0.25)",
    text: "hsl(var(--c-destructive))",
    icon: "hsl(var(--c-destructive))",
  },
  warning: {
    bg: "hsl(var(--c-warning) / 0.10)",
    border: "hsl(var(--c-warning) / 0.25)",
    text: "hsl(var(--c-warning))",
    icon: "hsl(var(--c-warning))",
  },
  info: {
    bg: "hsl(var(--c-info) / 0.10)",
    border: "hsl(var(--c-info) / 0.25)",
    text: "hsl(var(--c-info))",
    icon: "hsl(var(--c-info))",
  },
};

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [exiting, setExiting] = useState(false);
  const exitTimerRef = useRef<number | null>(null);
  const style = STYLES[toast.type];

  const handleDismiss = useCallback(() => {
    setExiting(true);
    exitTimerRef.current = window.setTimeout(() => onDismiss(toast.id), 250);
  }, [toast.id, onDismiss]);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current != null) {
        clearTimeout(exitTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(handleDismiss, toast.duration);
    return () => clearTimeout(t);
  }, [toast.duration, handleDismiss]);

  return (
    <div
      className={exiting ? "toast-exit" : "toast-enter"}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 8,
        background: style.bg,
        border: `1px solid ${style.border}`,
        color: style.text,
        fontSize: 13,
        fontWeight: 500,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        backdropFilter: "blur(8px)",
        minWidth: "min(240px, 100%)",
        maxWidth: "100%",
        pointerEvents: "auto",
        transform: exiting ? "translateY(-8px)" : "translateY(0)",
        opacity: exiting ? 0 : 1,
        transition: "transform 0.25s ease, opacity 0.25s ease",
      }}
    >
      <span style={{ color: style.icon, flexShrink: 0, marginTop: 1 }}>
        {ICONS[toast.type]}
      </span>
      <span style={{ flex: 1, lineHeight: 1.4 }}>{toast.message}</span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 2,
          color: style.text,
          opacity: 0.6,
          fontSize: 16,
          lineHeight: 1,
        }}
        onMouseEnter={(e) => {
          (e.target as HTMLButtonElement).style.opacity = "1";
        }}
        onMouseLeave={(e) => {
          (e.target as HTMLButtonElement).style.opacity = "0.6";
        }}
      >
        ×
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const show = useCallback(
    (message: string, type: ToastType = "info", duration = 4000) => {
      const id = `toast-${++idRef.current}`;
      setToasts((prev) => [...prev, { id, message, type, duration }]);
    },
    [],
  );

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = useCallback(
    (message: string, duration?: number) => show(message, "success", duration),
    [show],
  );
  const error = useCallback(
    (message: string, duration?: number) => show(message, "error", duration),
    [show],
  );
  const warning = useCallback(
    (message: string, duration?: number) => show(message, "warning", duration),
    [show],
  );
  const info = useCallback(
    (message: string, duration?: number) => show(message, "info", duration),
    [show],
  );

  const value = React.useMemo(
    () => ({ show, success, error, warning, info, dismiss }),
    [show, success, error, warning, info, dismiss],
  );

  _globalToast = value;

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          left: 16,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
